<template>
	<div class="page-layout" v-loading="loading">
		<gc-header headerData="pressureAnalysis">
			<template #ops>
				<el-button type="primary" @click="handleExport">导 出</el-button>
			</template>
		</gc-header>
		<div class="run-main">
			<div class="serach">
				<gc-advanced-filter v-model="form" :fields="filterFields" @search="handleQuery" @reset="pageInit" />
				<gc-column-setting
					:storageKey="`localcolumn-pressure-analysis`"
					:columns="columns"
					@update-column="data => (columns = data)"
				></gc-column-setting>
			</div>
			<div class="table-show">
				<gc-table
					:columns="columnList"
					:table-data="tableData"
					:border="true"
					:showPage="true"
					:total="page.total"
					:page-size="page.size"
					:currentPage="page.current"
					@current-page-change="
						currentPage => {
							currentPageChange(currentPage, apiPressureAnalysisPreview, 'date')
						}
					"
					@size-change="
						size => {
							page.size = size
							currentPageChange(1, apiPressureAnalysisPreview, 'date')
						}
					"
				></gc-table>
			</div>
		</div>

		<detail-dialog :show.sync="showDetailDialog" :detail="detail" :default-time="form.time"></detail-dialog>
	</div>
</template>

<script>
import { mapActions } from 'vuex'
import common from '../mixins/common'
import deviceTypeOption from '../mixins/deviceTypeOption'
import { apiPressureAnalysisPreview } from '@/apis/pressureAnalysis.api'
import { isObject } from '@/utils/validate'
import { exportReport } from '@/apis/statisticAnalysis.api'
import DetailDialog from './DetailDialog.vue'

const getDefaultTimeRange = () => {
	const end = new Date()
	const start = new Date()
	start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
	start.setHours(0, 0, 0)
	end.setHours(23, 59, 59)
	return [start, end]
}

const hasValidTimeRange = time => Array.isArray(time) && time.length === 2 && time.every(Boolean)

export default {
	name: 'pressureAnalysis',
	components: { DetailDialog },
	mixins: [common, deviceTypeOption],
	data() {
		return {
			form: {
				deviceNo: null,
				address: null,
				username: null,
				deviceTypeIdList: null,
				time: getDefaultTimeRange(),
			},
			searchCondition: [
				{ key: 'username', label: '客户/站点名称' },
				{
					key: 'deviceTypeIdList',
					dicKey: 'deviceTypeId',
					label: '设备类型',
					type: 'select',
					needAllForSearch: true,
					searchOptionKey: {
						label: 'deviceTypeName',
						value: 'deviceTypeId',
					},
					multiple: true,
				},
				{
					key: 'time',
					label: '日期区间',
					type: 'date',
					datePickerType: 'datetimerange',
					width: '450px',
					required: true,
					defaultValue: getDefaultTimeRange(),
				},
			],
			options: {},
			columns: [],
			detail: {},
			showDetailDialog: false,
		}
	},
	mounted() {
		this.pageInit()
		this.getDeviceTypeOptions()
	},
	methods: {
		...mapActions({
			getDeviceTypes: 'common/getAllDeviceTypeList',
		}),
		pageInit() {
			this.form = this.$options.data().form
			this.getList(1, apiPressureAnalysisPreview, 'date')
		},
		getDeviceTypeOptions() {
			this.getDeviceTypes().then(data => {
				this.$set(this.options, 'deviceTypeId', data)
			})
		},
		apiPressureAnalysisPreview,
		handleExport() {
			if (!this.tableData.length) return this.$message.warning('暂无表格数据')
			if (!hasValidTimeRange(this.form.time)) return this.$message.warning('请选择日期区间')
			this.$toast('下载中，请稍后...')
			const [startDate, endDate] = this.form.time
			const data = {
				method: 'post',
				url: '/monitor/report/pressure-analysis/export/excel',
				fileName: '压力分析.xlsx',
				data: {
					...this.form,
					startDate: this.dayjs(startDate).format('YYYY-MM-DD HH:mm:ss'),
					endDate: this.dayjs(endDate).format('YYYY-MM-DD HH:mm:ss'),
					time: undefined,
				},
			}
			exportReport(data, 'excel', null, this.$toast)
		},
		handleQuery() {
			if (!hasValidTimeRange(this.form.time)) return this.$message.warning('请选择日期区间')
			this.getList(1, apiPressureAnalysisPreview, 'date')
		},
		handleDetail(row) {
			this.detail = row
			this.showDetailDialog = true
		},
	},
	computed: {
		pressureUnitName() {
			return this.$store.getters.pressureUnitName
		},

		columnList() {
			return this.columns.filter(o => o.selected)
		},
		filterFields() {
			const keywordFields = [
				{ prop: 'deviceNo', label: '设备通讯编号', type: 'input' },
				{ prop: 'address', label: '地址', type: 'input' },
			]
			const conditionFields = this.searchCondition.map(item => {
				return {
					prop: item.key,
					label: item.label,
					type: item.type || 'input',
					defaultValue: item.defaultValue,
					props: {
						searchOption: this.options[item.dicKey || item.key],
						needAllForSearch: item.needAllForSearch,
						datePickerType: item.type === 'date' ? item.datePickerType : 'daterange',
						searchOptionKey: item.searchOptionKey,
						required: item.required,
						multiple: item.multiple,
					},
				}
			})
			return [...keywordFields, ...conditionFields]
		},
	},
	watch: {
		pressureUnitName: {
			handler() {
				this.columns = [
					{
						key: 'deviceNo',
						name: '设备通讯编号',
						fixed: 'left',
						dragDisable: true,
						disabledSelect: true,
					},
					// { key: 'designNo', name: '外观编号' },
					{ key: 'deviceTypeName', name: '设备类型' },
					{ key: 'username', name: '客户/站点名称' },
					{ key: 'phone', name: '联系电话' },
					{ key: 'address', name: '地址' },
					{
						key: 'underPressureMin',
						name: `压力下下限（${this.pressureUnitName}）`,
					},
					{
						key: 'underPressureMax',
						name: `压力下限（${this.pressureUnitName}）`,
					},
					{
						key: 'overPressureMin',
						name: `压力上限（${this.pressureUnitName}）`,
					},
					{
						key: 'overPressureMax',
						name: `压力上上限（${this.pressureUnitName}）`,
					},
					{ key: 'pressureMax', name: `最大压力（${this.pressureUnitName}）` },
					{ key: 'maxDataDate', name: '最大压力时间', width: '160' },
					{ key: 'pressureMin', name: `最小压力（${this.pressureUnitName}）` },
					{ key: 'minDataDate', name: '最小压力时间', width: '160' },
					{ key: 'pressureAvg', name: `压力均值（${this.pressureUnitName}）` },
					{
						key: 'oprate',
						name: '操作',
						minWidth: 200,
						render: (h, row) => {
							const childList = [
								h(
									'el-button',
									{
										props: {
											type: 'text',
											size: 'medium',
										},
										on: {
											click: () => this.handleDetail(row),
										},
									},
									'查看详情',
								),
							]
							const hasVNode = childList.some(item => isObject(item))
							return h('div', {}, hasVNode ? childList : '/')
						},
						fixed: 'right',
					},
				]
			},
			immediate: true,
		},
	},
}
</script>
<style lang="scss" scoped>
@import '../runLayout.scss';
.serach-ops {
	margin-bottom: 8px;
}
</style>
